import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { bouncieService } from "@/services/api";
import { dateTime, relativeTime } from "@/lib/format";

const POLL_MS = 5 * 60 * 1000;

/**
 * Says out loud when the Bouncie connection has stopped working.
 *
 * This exists because of a failure that was invisible by construction.
 * Bouncie's refresh tokens are single-use, so the grant died and every read
 * from their API started coming back invalid_grant — but webhooks carry no
 * token, so trips kept arriving on time and nothing looked broken. Odometer,
 * fuel, battery, trip reconciliation and the geo-zone push were frozen for
 * hours, and the only visible symptom was the app disagreeing with Bouncie's
 * own app. The person who noticed was the customer.
 *
 * A banner rather than an alert row: this is not an event on one vehicle, it
 * is the whole integration, and what it makes untrustworthy is whatever
 * number happens to be on screen. It says how stale the data is, because
 * "not connected" on its own does not tell you whether to trust what you are
 * reading — and it says what is still live, so the warning does not
 * overreach.
 */
export function IntegrationHealthBanner() {
  const { data: status, refetch } = useFetch(() => bouncieService.status(), []);

  // Polled so that reconnecting clears this without a page reload, and so a
  // grant that dies mid-session is announced rather than waited on.
  useEffect(() => {
    const id = setInterval(refetch, POLL_MS);
    return () => clearInterval(id);
  }, [refetch]);

  if (!status?.needs_reauth) return null;

  const staleSince = status.last_success_at;

  return (
    <div
      role="alert"
      className="border-b border-amber-400/25 bg-amber-400/10 px-4 py-3 sm:px-6 print:hidden"
      data-testid="bouncie-reauth-banner"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div className="min-w-0 text-sm">
            <p className="font-medium text-amber-200">
              Bouncie needs reconnecting — vehicle data on this page may be out of date
            </p>
            <p className="mt-0.5 text-xs text-amber-200/70">
              {staleSince
                ? `Odometer, fuel, battery and trip totals last updated ${relativeTime(staleSince)} (${dateTime(staleSince)}).`
                : "Odometer, fuel, battery and trip totals are not updating."}{" "}
              Live trip and geofence events are still arriving.
            </p>
          </div>
        </div>
        <Link
          to="/integrations"
          className="shrink-0 self-start rounded-md border border-amber-400/30 bg-amber-400/10 px-3 py-1.5 text-xs font-medium text-amber-200 transition-colors hover:bg-amber-400/20 sm:self-auto"
        >
          Reconnect
        </Link>
      </div>
    </div>
  );
}
